import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';

const TOUR_STORAGE_KEY = 'comply360_tour_completed';

const allSteps = [
  {
    target: '[data-tour="dashboard-header"]',
    title: 'Your Dashboard',
    content: 'Welcome to AIVORA Comply360! This is your central hub for managing ISO 27001:2022 maturity assessments.',
    position: 'bottom',
  },
  {
    target: '[data-tour="stats-grid"]',
    title: 'At-a-Glance Stats',
    content: 'Track your progress — total assessments, active work, completions, and organizations you\'re assessing.',
    position: 'bottom',
  },
  {
    target: '[data-tour="assessment-tracker"]',
    title: 'Assessment Tracker',
    content: 'Monitor your plan usage. See how many assessments you\'ve used vs. your limit.',
    position: 'bottom',
  },
  {
    target: '[data-tour="quick-actions"]',
    title: 'Quick Actions',
    content: 'Start assessments, view reports, and access admin tools from here.',
    position: 'left',
  },
  {
    target: '[data-tour="knowledge-base"]',
    title: 'Knowledge Base',
    content: 'Learn about ISO 27001:2022 controls, clauses, and best practices.',
    position: 'top',
    adminOnly: false,
  },
  {
    target: '[data-tour="audit-logs"]',
    title: 'Audit Logs',
    content: 'Track every system action — logins, assessments, changes. Great for compliance evidence.',
    position: 'top',
    adminOnly: true,
  },
  {
    target: '[data-tour="expert-services"]',
    title: 'Expert Services',
    content: 'Need help? Access ISO consulting and "Done-For-You" implementation services.',
    position: 'top',
  },
  {
    target: '[data-tour="request-more"]',
    title: 'Need More Assessments?',
    content: 'Click "Request More" to contact our team for additional assessments or custom plans.',
    position: 'top',
  },
];

const getTooltipPosition = (targetEl, position) => {
  if (!targetEl) return { top: 0, left: 0 };
  const rect = targetEl.getBoundingClientRect();
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;
  const gap = 14;

  switch (position) {
    case 'bottom':
      return { top: rect.bottom + scrollY + gap, left: rect.left + scrollX + rect.width / 2 };
    case 'top':
      return { top: rect.top + scrollY - gap, left: rect.left + scrollX + rect.width / 2 };
    case 'left':
      return { top: rect.top + scrollY + rect.height / 2, left: rect.left + scrollX - gap };
    case 'right':
      return { top: rect.top + scrollY + rect.height / 2, left: rect.right + scrollX + gap };
    default:
      return { top: rect.bottom + scrollY + gap, left: rect.left + scrollX + rect.width / 2 };
  }
};

const getTransformOrigin = (position) => {
  switch (position) {
    case 'bottom': return 'top center';
    case 'top': return 'bottom center';
    case 'left': return 'center right';
    case 'right': return 'center left';
    default: return 'top center';
  }
};

const getTranslate = (position) => {
  switch (position) {
    case 'bottom': return { x: '-50%', y: '0%' };
    case 'top': return { x: '-50%', y: '-100%' };
    case 'left': return { x: '-100%', y: '-50%' };
    case 'right': return { x: '0%', y: '-50%' };
    default: return { x: '-50%', y: '0%' };
  }
};

const WelcomeTour = ({ run, onComplete, isAdmin = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef(null);
  const tourStartedRef = useRef(false);

  const steps = useMemo(() => isAdmin ? allSteps : allSteps.filter(s => !s.adminOnly), [isAdmin]);

  const handleFinish = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setVisible(false);
    if (onComplete) onComplete();
  }, [onComplete]);

  const updatePosition = useCallback(() => {
    if (!run || currentStep >= steps.length) return;
    const step = steps[currentStep];
    const el = document.querySelector(step.target);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        const pos = getTooltipPosition(el, step.position);
        setTooltipPos(pos);
        setVisible(true);
      }, 400);
    } else {
      // Skip missing elements
      if (currentStep < steps.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        handleFinish();
      }
    }
  }, [run, currentStep, steps, handleFinish]);

  useEffect(() => {
    let timer;
    if (!run) {
      tourStartedRef.current = false;
    } else if (!tourStartedRef.current) {
      tourStartedRef.current = true;
      setCurrentStep(0);
      setVisible(false);
      timer = setTimeout(() => updatePosition(), 500);
    }
    return () => clearTimeout(timer);
  }, [run, updatePosition]);

  useEffect(() => {
    if (run) {
      setVisible(false);
      updatePosition();
    }
  }, [currentStep, updatePosition, run]);

  useEffect(() => {
    if (!run) return;
    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [run, updatePosition]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  const handleSkip = () => {
    handleFinish();
  };

  if (!run || !visible || currentStep >= steps.length) return null;

  const step = steps[currentStep];
  const translate = getTranslate(step.position);
  const targetEl = document.querySelector(step.target);

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[9998]" style={{ pointerEvents: 'none' }}>
        <div className="absolute inset-0 bg-black/40 transition-opacity duration-300" style={{ pointerEvents: 'auto' }} onClick={handleSkip} />
        {/* Spotlight cutout */}
        {targetEl && (
          <div
            className="absolute rounded-xl ring-4 ring-blue-400/60 shadow-2xl"
            style={{
              top: targetEl.getBoundingClientRect().top + window.scrollY - 8,
              left: targetEl.getBoundingClientRect().left + window.scrollX - 8,
              width: targetEl.getBoundingClientRect().width + 16,
              height: targetEl.getBoundingClientRect().height + 16,
              backgroundColor: 'transparent',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)',
              pointerEvents: 'none',
              zIndex: 9999,
            }}
          />
        )}
      </div>

      {/* Tooltip */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          ref={tooltipRef}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.25 }}
          className="fixed z-[10000] w-[340px] bg-white rounded-xl shadow-2xl border border-slate-200"
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            transform: `translate(${translate.x}, ${translate.y})`,
            transformOrigin: getTransformOrigin(step.position),
            pointerEvents: 'auto',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-blue-500" />
              <span className="font-semibold text-slate-900 text-sm">{step.title}</span>
            </div>
            <button onClick={handleSkip} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="px-5 pb-3">
            <p className="text-slate-600 text-sm leading-relaxed">{step.content}</p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 pb-4 pt-1 border-t border-slate-100 mt-1">
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === currentStep ? 'w-5 bg-blue-500' : 'w-1.5 bg-slate-200'}`} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <button onClick={handlePrev} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                  <ChevronLeft className="h-3 w-3" /> Back
                </button>
              )}
              {currentStep === 0 && (
                <button onClick={handleSkip} className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">
                  Skip
                </button>
              )}
              <button
                onClick={handleNext}
                className="flex items-center gap-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 px-4 py-1.5 rounded-lg transition-colors"
              >
                {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
                {currentStep < steps.length - 1 && <ChevronRight className="h-3 w-3" />}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};

export const isTourCompleted = () => localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
export const resetTour = () => localStorage.removeItem(TOUR_STORAGE_KEY);

export default WelcomeTour;
